import { ContentFooter } from "@app/components/core/ContentFooter/ContentFooter"
import { ContentHeader } from "@app/components/core/ContentHeader/ContentHeader"
import { Separator } from "@app/components/core/Separator/Separator"
import { Checkbox } from "@app/components/move2Library/Checkbox/Checkbox.jsx"
import { useCustomNavigation } from "@app/components/navigation/NavigationHandler"
import OrderContentBody from "@app/components/order/OrderContentBody/OrderContentBody"
import OrderSubPage from "@app/components/pageTypes/OrderSubPage/OrderSubPage"
import { OrderAnimationTypes } from "@app/components/routing/AnimatedOrderRoute/AnimatedOrderRoute"
import { setPayment } from "@app/store/actions/orderActions"
import { useGetPaymentGroups, useGetStrings } from "@app/store/service/cmsService"
import { useGetPaymentMethods, useGetSignedAmazonPayPayload } from "@app/store/service/connectorService"
import { appStore } from "@app/store/store"
import { createAmazonButtonConfig } from "@app/utils/amazon"
import { IframeMessages, ORDER_LINE_TYPES, PAYMENT_TYPES } from "@app/utils/constants"
import { useDelayLoading } from "@app/utils/hooks/useDelayLoading"
import { getPriceString } from "@app/utils/localeUtils"
import localStore from "@app/utils/localStore"
import { emptyArray } from "@app/utils/objectUtils"
import tracking from "@app/utils/tracking"
import { classNamez } from "@tecreation/funnel-components"
import { useSelector } from "@tecreation/js-state-react"
import { useCallback, useEffect, useState } from "react"
import Skeleton from "react-loading-skeleton"
import "./PaymentMethods.css"

export default function PaymentSelection() {
	const { push } = useCustomNavigation()

	const payment = useSelector(state => state.order.payment)
	const price = useSelector(state => state.order.formattedOrder.payAmount)
	const formattedOrder = useSelector(state => state.order.formattedOrder)
	const relation = useSelector(state => state.core.relation)
	const priceActions = useSelector(state => state.order.priceActions)
	const storePayPal = useSelector(state => state.order.storePayPal)

	const [amazonInfo, setAmazonInfo] = useState(undefined)
	const [thermenCardInfo, setThermenCardInfo] = useState(undefined)
	const [employeePointsInfo, setEmployeePointsInfo] = useState(undefined)

	const relLoggedIn = useSelector(state => state.core.flow == "relation")

	const { data: paymentGroups, __isLoading } = useGetPaymentGroups()
	const { data: savedPaymentMethods } = useGetPaymentMethods({}, { skip: !relLoggedIn })
	const { data: amazonPayload, isLoading: amazonLoading } = useGetSignedAmazonPayPayload(amazonInfo, { skip: amazonInfo === undefined })
	const {
		data: { buttonCheckout, paymentMethodsTitle, thermencardModalCredit, thermencardLocked, savePayPal, savePaypal },
	} = useGetStrings(undefined, { defaultData: {} })

	const isLoading = useDelayLoading(__isLoading)

	const hasThermencardLine = formattedOrder.orderLines.filter(e => e.details[0].typeId === ORDER_LINE_TYPES.Account)?.length ? true : false

	const handlePaymentChange = useCallback(
		(paymentGroupId, storedMethod) => {
			let pay
			appStore.set("order.storePayPal", false)
			if (paymentGroupId) {
				pay = paymentGroups.find(p => p.paymentGroup_id == paymentGroupId)
			} else if (storedMethod) {
				pay = {
					paymentId: storedMethod.paymentId,
					relationPaymentToken: storedMethod.relationPaymentToken,
					paymentGroup: storedMethod.paymentDetail + " (" + storedMethod.displayText + ")",
				}
			}
			setPayment(pay)
		},
		[paymentGroups],
	)

	useEffect(() => {
		if (savedPaymentMethods && payment == undefined && Array.isArray(savedPaymentMethods) && savedPaymentMethods.length > 0) {
			handlePaymentChange(undefined, savedPaymentMethods[0])
			document.getElementById(savedPaymentMethods[0].relationPaymentToken).checked = true
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [savedPaymentMethods, payment])

	useEffect(() => {
		if (paymentGroups) {
			let amazon = paymentGroups.find(e => e.type === PAYMENT_TYPES.AmazonPay)
			if (amazon) {
				setAmazonInfo({
					payAmount: price,
					paymentId: amazon.paymentId,
				})
			}
			let thermencard = paymentGroups.find(e => e.type === PAYMENT_TYPES.Thermencard)
			let epp = paymentGroups.find(e => e.type === PAYMENT_TYPES.EmployeePoints)
			if (thermencard && relation && relation.thermencardAccount) {
				setThermenCardInfo(relation.thermencardAccount)
			}
			if (epp && relation && relation.employeePoints) {
				setEmployeePointsInfo(relation.employeePoints)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paymentGroups])

	useEffect(() => {
		if (amazonPayload) {
			if (window.self !== window.top) {
				window.parent.postMessage(IframeMessages.setAmazonButtonData + ";" + JSON.stringify(createAmazonButtonConfig(appStore.get("core.locale"))), "*")
			} else {
				window.amazonPayBtn = window.amazon.Pay.renderButton("#AmazonPayButton", createAmazonButtonConfig(appStore.get("core.locale")))
			}
		}
	}, [amazonPayload])

	const next = useCallback(() => {
		if (payment) {
			tracking.addPaymentInfo(formattedOrder, payment.type)

			if (payment.type === PAYMENT_TYPES.AmazonPay) {
				localStore.setItem("formattedOrder", JSON.stringify({ ...formattedOrder, paymentId: payment.paymentId }))
				localStore.setItem("priceActions", JSON.stringify(priceActions))
				let config = {
					estimatedOrderAmount: { amount: price, currencyCode: "EUR" },
					createCheckoutSessionConfig: {
						payloadJSON: JSON.stringify(amazonPayload.payload),
						signature: amazonPayload.signature,
						publicKeyId: appStore.get("settings.amazonPublicKeyId"),
					},
				}
				if (window.self !== window.top) {
					window.parent.postMessage(IframeMessages.initinateAmazonPay + ";" + JSON.stringify(config), "*")
				} else {
					window.amazonPayBtn.initCheckout(config)
				}
			} else {
				if (appStore.get("core.invoiceAddress") && appStore.get("core.invoiceAddress").email && appStore.get("core.invoiceAddress").email.length) {
					push("/order/next-order-overview", OrderAnimationTypes.fullSlide)
				} else {
					push("/order/address-details", OrderAnimationTypes.fullSlide)
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [payment, price, formattedOrder, priceActions])

	return (
		<OrderSubPage>
			<ContentHeader canGoBack colorStyle="white" animation={OrderAnimationTypes.fullSlide}>
				{paymentMethodsTitle}
			</ContentHeader>
			<OrderContentBody>
				{savedPaymentMethods?.length > 0 && (
					<>
						<div className="f4 regular mb-2">Saved Payment Methods</div>
						<Separator />
						{savedPaymentMethods.map((method, index) => (
							<div className="mb-5" key={index}>
								<label htmlFor={method.relationPaymentToken} className="f4 flex flex-items-center">
									<input
										type="radio"
										id={method.relationPaymentToken}
										name="paymentChoice"
										value={method.relationPaymentToken}
										onChange={() => {
											handlePaymentChange(undefined, method)
										}}
										defaultChecked={payment?.relationPaymentToken === method.relationPaymentToken}
									/>
									<span className="ml-2">{`${method.paymentDetail} (${method.displayText})`}</span>
								</label>
							</div>
						))}
					</>
				)}
				<div style={{ display: "none" }} id="AmazonPayButton" />
				{isLoading && (
					<>
						{emptyArray(4).map((__, i) => (
							<div className="flex mt-2" key={i}>
								<div style={{ width: "16px", height: "16px" }} className="mr-2">
									<Skeleton />
								</div>
								<div className="w-50">
									<Skeleton />
								</div>
							</div>
						))}
					</>
				)}
				{savedPaymentMethods?.length > 0 && (
					<>
						{" "}
						<div className="f4 regular mb-2">Another Payment Methods</div> <Separator />
					</>
				)}

				{!isLoading &&
					paymentGroups &&
					!appStore.get("core.kioskMode") &&
					paymentGroups.map((paymentMethod, index) => {
						const userAgent = navigator.userAgent || window.opera
						const isAndroid = /android/i.test(userAgent)
						const showApplePay = window.ApplePaySession
						const showGooglePay = isAndroid
						let show = false
						let showStorage = false
						if (paymentMethod.type === PAYMENT_TYPES.ApplePay) {
							show = showApplePay
						} else if (paymentMethod.type === PAYMENT_TYPES.GooglePay) {
							show = showGooglePay
						} else if (paymentMethod.type === PAYMENT_TYPES.Thermencard) {
							show = thermenCardInfo ? true : false
						} else if (paymentMethod.type === PAYMENT_TYPES.EmployeePoints) {
							show = employeePointsInfo ? true : false
						} else if (paymentMethod.type === PAYMENT_TYPES.Invoice) {
							show = relation.billable ? true : false
						} else if (paymentMethod.type === PAYMENT_TYPES.AmazonPay) {
							show = localStore.isAccessable()
						} else if (paymentMethod.type == PAYMENT_TYPES.PayPal) {
							show = true
							showStorage = paymentMethod.funnels[0]?.paymentDetailIds[0].payment_id.cacheable && relLoggedIn
						} else {
							show = true
						}

						if (show) {
							const isThermencard = paymentMethod.type === PAYMENT_TYPES.Thermencard
							const thermenCardDisabled = thermenCardInfo ? (thermenCardInfo.lock === true ? true : thermenCardInfo.amount < formattedOrder.payAmount) : true

							const isEmployeePoints = paymentMethod.type === PAYMENT_TYPES.EmployeePoints
							const employeePointsDisabled = employeePointsInfo ? employeePointsInfo.amount < formattedOrder.payAmount : true

							let isDisabled = (isThermencard && hasThermencardLine) || (isThermencard && thermenCardDisabled) || (isEmployeePoints && employeePointsDisabled)

							return (
								<div className="mb-5" key={index}>
									<label htmlFor={paymentMethod.paymentGroup} className="f4 flex flex-items-center">
										<input
											type="radio"
											id={paymentMethod.paymentGroup}
											name="paymentChoice"
											value={isDisabled ? "not again" : paymentMethod.paymentGroup_id}
											defaultChecked={payment && paymentMethod.paymentGroup_id === payment.paymentGroup_id}
											onChange={e => {
												handlePaymentChange(e.target.value, undefined, undefined)
											}}
											disabled={isDisabled}
										/>
										<span className={"ml-2" + classNamez({ "color-fg-neutral-700": isDisabled })}>
											{paymentMethod.paymentGroup} {thermenCardDisabled && isThermencard && thermenCardInfo.lock && " - " + thermencardLocked}
											{((thermenCardDisabled || thermenCardInfo?.lock) && isThermencard) || (isEmployeePoints && employeePointsDisabled)
												? `- ${thermencardModalCredit}: ` +
													(isThermencard ? getPriceString(thermenCardInfo.amount, 2) : getPriceString(employeePointsInfo.amount, 2))
												: ""}
											{/* <img style={{ width: "auto", height: "45px" }} src={paymentMethod.image} /> */}
										</span>
									</label>
									{showStorage && (
										<Checkbox
											name="storePayment"
											className="ml-4 mt-2"
											label={savePayPal || savePaypal}
											checked={storePayPal}
											formRegister={{ name: "storePayment" }}
											onChange={e => {
												appStore.set("order.storePayPal", e.target.checked)
											}}
										/>
									)}
								</div>
							)
						} else {
							return null
						}
					})}

				<div id="AmazonPayButton" style={{ display: "none" }} />
			</OrderContentBody>

			<ContentFooter
				roundPrice={false}
				price={price}
				priceAbsolute
				buttonProps={{ text: buttonCheckout, onNext: next, disabled: !payment, isLoading: isLoading || amazonLoading }}
				process={6}
			/>
		</OrderSubPage>
	)
}
